import { useEffect, useId, useRef } from 'react';

// CB-17 layer stack (VIS §7.2 row "Layer stack"). Every overlay — sheet, dialog,
// drawer, dropdown, kebab menu, zoom overlay — registers here while it is open, so
// the console has ONE policy for:
//   - Escape closes the TOP layer only (a listbox inside a dialog closes first);
//   - body scroll is locked while any blocking layer is open, restored after;
//   - focus returns to whatever was focused when the layer opened;
//   - an optional back-button guard: the browser Back button closes the top layer
//     instead of leaving the page (opt-in per layer, `guardBack`).
// Toasts are not layers (they never take Escape). Module-level on purpose: one
// document listener, no provider to forget; `__resetLayers` exists for tests.

interface Layer {
  id: string;
  close: () => void;
  blocking: boolean;
  guardBack: boolean;
}

const stack: Layer[] = [];
let savedOverflow: string | null = null;
let listening = false;

// DC123: Tab / Shift+Tab stay inside the topmost open modal (`aria-modal="true"`, last
// in document order — overlays portal/append after their opener) while a blocking
// layer is open. Found from the DOM rather than a ref so every existing overlay gets
// it without an API change.
const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function trapTab(e: KeyboardEvent) {
  if (!stack.some((l) => l.blocking)) return;
  const modals = document.querySelectorAll<HTMLElement>('[aria-modal="true"]');
  const root = modals[modals.length - 1];
  if (!root) return;
  const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.closest('[hidden],[aria-hidden="true"]') && el.getClientRects().length > 0,
  );
  if (items.length === 0) return;
  const first = items[0]!;
  const last = items[items.length - 1]!;
  const active = document.activeElement;
  const inside = active instanceof Node && root.contains(active);
  if (e.shiftKey && (active === first || !inside)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && (active === last || !inside)) {
    e.preventDefault();
    first.focus();
  }
}

function onKeyDown(e: KeyboardEvent) {
  if (e.key === 'Tab') {
    trapTab(e);
    return;
  }
  if (e.key !== 'Escape' || stack.length === 0) return;
  const top = stack[stack.length - 1];
  e.preventDefault();
  e.stopPropagation();
  top?.close();
}

function onPopState() {
  const top = stack[stack.length - 1];
  if (top?.guardBack) top.close();
}

function syncScrollLock() {
  if (typeof document === 'undefined') return;
  const anyBlocking = stack.some((l) => l.blocking);
  if (anyBlocking && savedOverflow === null) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  } else if (!anyBlocking && savedOverflow !== null) {
    document.body.style.overflow = savedOverflow;
    savedOverflow = null;
  }
}

function ensureListening() {
  if (listening || typeof document === 'undefined') return;
  document.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('popstate', onPopState);
  listening = true;
}

export function isTopLayer(id: string): boolean {
  return stack[stack.length - 1]?.id === id;
}

export function layerDepth(): number {
  return stack.length;
}

/** Test-only: drop every registered layer and restore the body. */
export function __resetLayers(): void {
  stack.length = 0;
  syncScrollLock();
}

export interface LayerOptions {
  /** Lock body scroll while open (dialogs, sheets, drawers). Popovers pass false. */
  blocking?: boolean;
  /** Browser Back closes this layer instead of navigating away. */
  guardBack?: boolean;
  /** Return focus to the element focused at open time (default true). */
  restoreFocus?: boolean;
}

/**
 * Register an overlay while `open`. Returns the layer id (stable per component).
 * `onClose` is always read fresh, so callers can pass an inline arrow.
 */
export function useLayer(open: boolean, onClose: () => void, options: LayerOptions = {}): string {
  const id = useId();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const { blocking = true, guardBack = false, restoreFocus = true } = options;

  useEffect(() => {
    if (!open) return;
    ensureListening();
    const opener =
      typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;
    const layer: Layer = { id, close: () => closeRef.current(), blocking, guardBack };
    stack.push(layer);
    syncScrollLock();
    if (guardBack && typeof history !== 'undefined') {
      history.pushState({ ...(history.state ?? {}), bdLayer: id }, '');
    }
    return () => {
      const i = stack.findIndex((l) => l.id === id);
      if (i >= 0) stack.splice(i, 1);
      syncScrollLock();
      // Closed from the UI (not via Back): drop the guard entry we pushed.
      if (guardBack && typeof history !== 'undefined' && history.state?.bdLayer === id)
        history.back();
      if (restoreFocus && opener && typeof opener.focus === 'function' && opener.isConnected)
        opener.focus();
    };
  }, [open, id, blocking, guardBack, restoreFocus]);

  return id;
}
